import { Zap, TrendingUp, Shield, Clock, MapPin, BarChart3, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem, fadeInLeft } from "@/components/animations/ScrollAnimations";

import chargingSite1 from "@/assets/charging-site-1.jpg";
import chargingSite2 from "@/assets/charging-site-2.jpg";
import chargingSite3 from "@/assets/charging-site-3.jpg";
import chargingSite4 from "@/assets/charging-site-4.jpg";
import chargingSite5 from "@/assets/charging-site-5.jpg";
import chargingSite6 from "@/assets/charging-site-6.jpg";

const cpoFeatures = [
  {
    icon: TrendingUp,
    title: "Revenue Optimization",
    description: "Dynamic pricing and demand management to maximize returns",
  },
  {
    icon: Shield,
    title: "NEVI, State and Federal Compliant",
    description: "Meet all State and federal requirements for infrastructure funding",
  },
  {
    icon: Clock,
    title: "98.5% Uptime",
    description: "Industry-leading reliability with proactive monitoring",
  },
  {
    icon: BarChart3,
    title: "Real-time Analytics",
    description: "Comprehensive dashboards for network performance",
  },
];

const chargingSites = [
  { image: chargingSite1, location: "Hotel & Hospitality", type: "DC Fast Charging" },
  { image: chargingSite2, location: "Commercial Parking", type: "Level 2 & DC Fast" },
  { image: chargingSite3, location: "Highway Corridor", type: "DC Fast Charging" },
  { image: chargingSite4, location: "Retail Center", type: "Multi-port Station" },
  { image: chargingSite5, location: "Multi-family Housing", type: "Level 2 Charging" },
  { image: chargingSite6, location: "Mixed-use Development", type: "DC Fast Charging" },
];

export function CPOShowcaseSection() {
  return (
    <section id="cpo" className="py-28 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-subtle" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 mb-16">
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <ScrollReveal variants={fadeInLeft}>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Charge Point <span className="text-gradient">Operators</span>
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Build, manage, and scale your EV charging network with our enterprise-grade CPO platform. From site
                planning to revenue optimization, we've got you covered.
              </p>

              <motion.div
                className="flex flex-col gap-4"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-baseline gap-4">
                  <span className="text-5xl font-bold text-primary">50+</span>
                  <span className="text-muted-foreground">Sites Deployed</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-5xl font-bold text-accent">10K+</span>
                  <span className="text-muted-foreground">Charging Sessions/Month</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <Button variant="hero" className="mt-8 group">
                  Become a CPO Partner
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </motion.div>
            </ScrollReveal>
          </div>

          {/* CPO Features Grid */}
          <div className="lg:col-span-7">
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 gap-4" staggerDelay={0.08}>
              {cpoFeatures.map((feature, idx) => (
                <StaggerItem key={idx}>
                  <motion.div
                    whileHover={{ scale: 1.03, y: -4 }}
                    transition={{ duration: 0.2 }}
                    className="group bg-card/50 border border-border/50 rounded-2xl p-6 hover:border-primary/30 hover:bg-card transition-all duration-300"
                  >
                    <motion.div
                      className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <feature.icon className="h-6 w-6 text-primary" />
                    </motion.div>
                    <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>

        {/* Charging Sites Gallery */}
        <div className="mt-20">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Our <span className="text-gradient">Deployed Sites</span>
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Real charging infrastructure deployed across North America, serving thousands of EV drivers daily.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 gap-4" staggerDelay={0.1}>
            {chargingSites.map((site, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  className="group relative rounded-2xl overflow-hidden aspect-[4/3] cursor-pointer focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-background"
                  tabIndex={0}
                  role="figure"
                  aria-label={`${site.location} - ${site.type} charging station`}
                >
                  <img
                    src={site.image}
                    alt={`${site.location} EV charging station featuring ${site.type}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <div className="flex items-center gap-2 text-primary mb-1">
                      <MapPin className="h-4 w-4" />
                      <span className="text-sm font-medium">{site.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Zap className="h-4 w-4" />
                      <span className="text-xs">{site.type}</span>
                    </div>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </div>
    </section>
  );
}
